import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'

/**
 * Button style variants, on Plan View's button recipe:
 *   default     → primary: the one filled action in a view
 *   outline     → secondary: white on the control line, with a hairline shadow
 *   destructive → the confirming destructive button — in the dialog, not on the
 *                 page, where "Delete" is a secondary in danger ink
 *   ghost       → quiet: muted ink until hovered
 *   link        → an inline link, in `accent-ink`
 * `size` sets height: 36px by default, 30px `sm` (table bars, filters, callout
 * actions), 40px `lg` for a lone action on a sign-in or empty page. `className`
 * wins over the base set (see `cn`).
 */
export const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium leading-none transition-[background-color,border-color,color,box-shadow] duration-100 focus-visible:focus-ds disabled:pointer-events-none disabled:opacity-50 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-primary text-on-primary hover:bg-primary-hover',
        destructive: 'bg-danger-fill text-on-danger hover:brightness-[0.94]',
        outline: 'border border-line-control bg-surface text-ink shadow-xs hover:bg-surface-hover',
        ghost: 'text-ink-muted hover:bg-surface-hover hover:text-ink',
        link: 'text-accent-ink underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-9 px-3.5',
        sm: 'h-[30px] gap-1.5 px-2.5 text-control',
        lg: 'h-10 px-5',
        icon: 'h-9 w-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  /** Render the button's styles onto its single child instead of a `<button>`. */
  asChild?: boolean
  /**
   * The action is under way: the button is disabled, tells assistive tech it is
   * busy, and its leading icon turns into a spinner (or gains one, when it has
   * no icon). Not for `asChild`, whose single child is not ours to rearrange.
   */
  pending?: boolean | undefined
}

/**
 * The children with a spinner in place of the leading icon. An icon is any
 * component element at the front (`<Plus />`); a host element such as a `<span>`
 * holding the label is words, so the spinner goes before it instead.
 */
function withSpinner(children: React.ReactNode): React.ReactNode[] {
  const items = React.Children.toArray(children)
  const spinner = <Loader2 key="pending" aria-hidden className="h-4 w-4 animate-spin" />
  const [first, ...rest] = items
  if (React.isValidElement(first) && typeof first.type !== 'string') return [spinner, ...rest]
  return [spinner, ...items]
}

/**
 * A styled button. Pass `asChild` to apply button styling to a child element
 * (e.g. a router `<Link>`) via Radix `Slot`, and `pending` while its action runs.
 */
export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  function Button({ className, variant, size, asChild = false, pending = false, disabled, children, ...props }, ref) {
    const Comp = asChild ? Slot : 'button'
    return (
      <Comp
        className={cn(buttonVariants({ variant, size }), className)}
        ref={ref}
        disabled={disabled === true || pending}
        aria-busy={pending ? true : undefined}
        {...props}
      >
        {pending && !asChild ? withSpinner(children) : children}
      </Comp>
    )
  },
)
